import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { LocalConfig } from 'utils';

import {
  CrmApi,
  isEmptyCollection,
  NotificationManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { Button, EmptyStateView, Label } from '@atlas/ui';

import DatePicker from 'common/DatePicker/DatePicker';
import LuckyDayBack from 'components/LuckyDay/LuckyDayBack';
import LuckyDayBanner from 'components/LuckyDay/LuckyDayBanner';
import LuckyDayDates from 'components/LuckyDay/LuckyDayDates';
import LuckyDayDescription from 'components/LuckyDay/LuckyDayDescription';
import { LuckyDayPopup } from 'components/LuckyDay/LuckyDayPopup';
import SeoPageText from 'components/SeoPage/SeoPageText';

import ShortFooter from '../Footer/ShortFooter';

import PreviousWinners from './PreviousWinners';
import TodayWinners from './TodayWinners';

const dateFormat = 'YYYY-MM-DD';

const LuckyDay = () => {
  const { lang } = useSettings();
  const yesterday = dayjs().subtract(1, 'days').endOf('day');
  const [tournament, setTournament] = useState({});
  const [todayHistoryIds, setTodayHistoryIds] = useState(null);
  const [datePickerHistoryIds, setDatePickerHistoryIds] = useState(null);
  const [todayShowMore, setTodayShowMore] = useState(false);
  const [selectedShowMore, setSelectedShowMore] = useState(false);
  const [date, setDate] = useState(yesterday.format(dateFormat));
  const [error, setError] = useState(false);
  const [isLoadedTournament, setIsLoadedTournament] = useState(false);
  const { account, authenticated } = useApplicationState();

  const { description, endTime, headBanner, id, name, shortDescription, startTime, status } =
    tournament;

  const projectId = LocalConfig.get('projectId');

  const handleShowMore = (state, setState) => () => setState(state => !state);

  const getPostActivatedLuckyDay = useCallback(() => {
    CrmApi.postActivatedLuckyDay(projectId)
      .then(tournamentData => {
        setTournament(tournamentData.data[0] || {});
        setIsLoadedTournament(true);
      })
      .catch(() => {
        setError(true);
        setIsLoadedTournament(false);
      });
  }, [projectId]);

  useEffect(() => {
    setError(false);

    getPostActivatedLuckyDay();
  }, [getPostActivatedLuckyDay, lang, account.id]);

  useEffect(() => {
    if (id) {
      CrmApi.postHistoryLuckyDay(id, dayjs().format(dateFormat)).then(historyIds => {
        if (!historyIds.data.length) return setTodayHistoryIds([]);

        const prepareHistoryIds = historyIds.data[0].clients.reduce((listIds, currentId) => {
          listIds.push(Object.values(currentId)[0]);

          return listIds;
        }, []);

        setTodayHistoryIds(prepareHistoryIds);
      });
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      CrmApi.postHistoryLuckyDay(id, date).then(historyIds => {
        if (!historyIds.data.length) return setDatePickerHistoryIds([]);

        const prepareHistoryIds = historyIds.data[0].clients.reduce((listIds, currentId) => {
          listIds.push(Object.values(currentId)[0]);

          return listIds;
        }, []);

        setDatePickerHistoryIds(prepareHistoryIds);
      });
    }
  }, [date, id]);

  const [showAuthPopup, setShowAuthPopup] = useState(false);

  const handleAuthPopupOpen = () => {
    setShowAuthPopup(true);
  };

  const handleCloseLuckyDayPopup = () => {
    setShowAuthPopup(false);
  };

  if (error) {
    return (
      <div className="lucky-day__container">
        <EmptyStateView
          className="absolute"
          description="lucky_day_error_message"
          title="lucky_day_error_title"
        />
      </div>
    );
  }

  if (isEmptyCollection(tournament) && isLoadedTournament) {
    return (
      <>
        <div className="lucky-day__container">
          <EmptyStateView
            className="absolute"
            description="lucky_day_empty_message"
            title="lucky_day_empty_title"
          />
        </div>
        <ShortFooter />
      </>
    );
  }

  const prepareHistoryListIds = dataListIds =>
    dataListIds &&
    dataListIds.reduce((listIds, currentId, index) => {
      const chunkIndex = Math.floor(index / 5);

      if (!listIds[chunkIndex]) {
        listIds[chunkIndex] = [];
      }

      listIds[chunkIndex].push(currentId);

      return listIds;
    }, []);

  const isTournamentWinner = todayHistoryIds && todayHistoryIds.some(id => +id === account.id);

  const handleActivate = () => {
    CrmApi.getActiveLuckyDay(id).then(() => {
      getPostActivatedLuckyDay();
    });

    NotificationManager.notify({
      message: 'bonus_activated',
      status: 'success',
      type: 'notification',
    });
  };

  const selectedWinnersList = prepareHistoryListIds(datePickerHistoryIds);
  const todayWinnersList = prepareHistoryListIds(todayHistoryIds);

  const isOutsideRange = {
    from: dayjs(startTime).add(1, 'day'),
    to: yesterday,
  };

  return (
    <>
      {!isEmptyCollection(tournament) && (
        <div className="layout-to-scroll">
          <div className="promotion_page single luckyday">
            <div className="promotion">
              <div className="promotion__item">
                <div className="content static_page">
                  <LuckyDayBack name={name} />
                  <LuckyDayBanner headBanner={headBanner} />
                  <LuckyDayDates endTime={endTime} startTime={startTime} />

                  {shortDescription && (
                    <div className="description short-content">{shortDescription}</div>
                  )}

                  <LuckyDayDescription description={description} handleShowMore={handleShowMore} />

                  <TodayWinners
                    handleShowMore={handleShowMore(todayShowMore, setTodayShowMore)}
                    isShowMore={todayShowMore}
                    winnersList={todayWinnersList}
                  >
                    <h2>
                      <Label
                        className="luckyday-winners__header"
                        message="tournament_today_winners"
                      />
                    </h2>
                  </TodayWinners>

                  <PreviousWinners
                    handleShowMore={handleShowMore(selectedShowMore, setSelectedShowMore)}
                    isShowMore={selectedShowMore}
                    winnersList={selectedWinnersList}
                  >
                    <h2>
                      <Label
                        className="luckyday-winners__header"
                        message="tournament_previous_winners"
                      />
                    </h2>

                    <DatePicker date={date} isOutsideRange={isOutsideRange} setDate={setDate} />
                  </PreviousWinners>

                  {isTournamentWinner && status === 'AVAILABLE_TO_ACTIVATE' && (
                    <div className="tournaments-box get-bonus">
                      <Button
                        className="btn primary big "
                        label="lucky_day_get_bonus"
                        onClick={handleActivate}
                      />
                    </div>
                  )}

                  {!authenticated && (
                    <div className="tournaments-box participate_btn">
                      <Button className="btn primary big" onClick={handleAuthPopupOpen}>
                        <Label message="lucky_day_participate" />
                      </Button>
                    </div>
                  )}
                  {showAuthPopup && (
                    <LuckyDayPopup handleCloseLuckyDayPopup={handleCloseLuckyDayPopup} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <SeoPageText />
        </div>
      )}
      <ShortFooter />
    </>
  );
};

export default LuckyDay;
